import { defineStore } from 'pinia'
import type { AppUser, PublicAppRuntimeConfig } from '@packages/app-config'
import { LocaleCode, getLocaleCountryCode, isLocaleCode } from '@packages/i18n'
import areaCodeEn from '../data/areaCodeEn.json'

export type RuntimeContextPayload = Partial<PublicAppRuntimeConfig>

export type UserCountryProfile = {
  code: string
  countryCode: string
  twoCharCode: string
  name: string
  flag: string
  [key: string]: any
}

export type BusinessState = {
  isWiki: string
  LanguageCode: LocaleCode | ''
  user: AppUser | null
  serverbfp: string
  cookieHost: string
  myCountry: string
  CountryCode: string
  userIp: string
  nodeEnv: string
  nowtime: string
  loginShow: boolean
  loginverify: boolean
  userlogin2: boolean
  nestinghref: string
  usergetCountry: UserCountryProfile
}

export type SharedAppState = {
  // 运行时配置
  apiBase: string
  appEnv: string
  platformName: string
  siteName: string
  hydrateOnVisibleRootMargin: number
  host: string
  allHost: string
  path: string
  suffix: string
  contentcookie: string
  riskcontrol: Record<string, any>
  skylinecheck: Record<string, any>
  newhost: string
  langList: string[]
  menuData: any[]
  navList: any[]
  visibleList: any[]
  invisibleList: any[]
  langshow: boolean
  userVip: boolean
  videoshow: boolean
  email: string
  islangsw: boolean
  wordid: string
  livestate: {
    livestatus?: number
    count?: number
  }
  bfp: string
  searchWord: string
  newsbool: boolean
  linkFriend: Array<{ name: string; link: string }>
  partnerList: Array<{ name: string; link: string }>
  nonCooperativeTraders: Array<{ code?: string; traderCode: string; traderName: string }>
  brokerwikifxarr: Array<{ broker_code: string; broker_name: string }>
} & BusinessState

// 将 payload 中非 undefined 的字段合并到目标对象
const patchDefined = (target: any, payload: Record<string, unknown>) => {
  for (const [key, value] of Object.entries(payload)) {
    if (value !== undefined) target[key] = value
  }
}

type CountryCodeItem = {
  countryCode?: string
  twoCharCode?: string
}

type CountryCodeGroup = {
  data?: CountryCodeItem[]
}

const countryCodeItems = (areaCodeEn as CountryCodeGroup[]).flatMap((group) => group.data || [])
const countryNumericCodeMap = new Map(
  countryCodeItems
    .map((item) => [item.twoCharCode?.toLowerCase() || '', item.countryCode || ''] as const)
    .filter(([twoCharCode, countryCode]) => twoCharCode && countryCode),
)
const countryAlphaCodeMap = new Map(
  countryCodeItems
    .map((item) => [item.countryCode || '', item.twoCharCode?.toLowerCase() || ''] as const)
    .filter(([countryCode, twoCharCode]) => countryCode && twoCharCode),
)
export const DEFAULT_COUNTRY_CODE = getLocaleCountryCode(LocaleCode.En) || '840'

export const normalizeCountryCodeValue = (value: unknown) => {
  const rawCountryCode =
    typeof value === 'string' ? value.trim().replace(/^"+|"+$/g, '') : typeof value === 'number' ? String(value) : ''
  if (!rawCountryCode) {
    return ''
  }

  if (/^\d+$/.test(rawCountryCode)) {
    return rawCountryCode
  }

  const twoCharCode = rawCountryCode.toLowerCase() === 'gb' ? 'uk' : rawCountryCode.toLowerCase()
  return countryNumericCodeMap.get(twoCharCode) || twoCharCode
}

export const normalizeCountryStoreCode = (value: unknown) => {
  const countryCode = normalizeCountryCodeValue(value)
  if (countryAlphaCodeMap.has(countryCode)) return countryCode

  const localeCountryCode = getLocaleCountryCode(value)
  return localeCountryCode && countryAlphaCodeMap.has(localeCountryCode) ? localeCountryCode : DEFAULT_COUNTRY_CODE
}

export const resolveCountryCookieValue = (value: unknown) => {
  return countryAlphaCodeMap.get(normalizeCountryStoreCode(value)) || 'us'
}

export const useAppStore = defineStore('shared-app', {
  state: (): SharedAppState => ({
    // 运行时配置
    apiBase: '',
    appEnv: '',
    platformName: '',
    siteName: '',
    hydrateOnVisibleRootMargin: 300,
    // 业务状态
    isWiki: '1', // (1:wikifx;2:包含普通fxeye；3:包含wikifxvn.com,wikifxvi.com,wikifxen.com, 4:fxeye中推广用的特殊域名)
    LanguageCode: '',
    user: null,
    serverbfp: '',
    cookieHost: '',
    myCountry: '',
    CountryCode: '',
    userIp: '',
    nodeEnv: '',
    nowtime: '',
    loginShow: false,
    loginverify: false,
    userlogin2: false,
    nestinghref: '',
    usergetCountry: {
      code: '0086',
      countryCode: '156',
      twoCharCode: 'CN',
      name: 'China',
      flag: 'https://img.souhei.com.cn/flag/7d8833382673bab2/CN.png_wiki-template-global',
    },
    host: '',
    allHost: '',
    path: '', // 当前页面路径（不包含语言）
    suffix: 'com', // 域名后缀
    contentcookie: '', // 用户登录cookie值
    riskcontrol: {}, // 屏蔽点位
    skylinecheck: {}, // skyline年份+国家+是否展示
    newhost: '', // 新主域名
    langList: [
      'ar', // 阿拉伯语
      'de', // 德语
      'en',
      'es',
      'fil',
      'fr', // 法语
      'hi', // 印地
      'id',
      'it', // 意大利
      'ja',
      'ko', // 韩语
      'pt', // 葡萄牙语
      'tr', // 土耳其
      'ru',
      'th',
      'vi',
      'ur',
      'zh',
      'zh-cn',
      'zh-hk',
      'zh-tw',
    ], // 语言列表
    menuData: [], // 原始菜单数据
    navList: [], // 导航条数据
    visibleList: [], // 可见区域导航项
    invisibleList: [], // 隐藏区域导航项
    langshow: false, // 语言框显示隐藏
    userVip: false, // 是否为VIP用户
    videoshow: false, // 视频显示隐藏
    email: '', // 用户邮箱
    islangsw: false, // 是否展示语言切换
    wordid: '', // 是否存在文字版交易商
    livestate: {}, // 直播状态 1-进行中；2-未开始；3-已结束
    bfp: '', // 指纹
    searchWord: '', // 顶部搜索输入的内容
    newsbool: false,
    linkFriend: [],
    partnerList: [],
    nonCooperativeTraders: [],
    brokerwikifxarr: [],
  }),
  getters: {
    brandName: (state) => [state.siteName, state.platformName].filter(Boolean).join(' '),
  },
  actions: {
    setLanguageCode(locale: LocaleCode) {
      if (locale === 'zh-hk' || locale === 'zh-tw') {
        this.LanguageCode = 'zh' as LocaleCode
      } else {
        this.LanguageCode = locale
      }
    },
    setRuntimeContext(payload: RuntimeContextPayload) {
      patchDefined(this, payload)
    },
    setBusinessState(payload: Partial<SharedAppState>) {
      patchDefined(this, payload)
    },
    setHostend(value: string) {
      // 设置域名后缀
      this.host = value
    },
    setAllhost(value: string) {
      // 获取当前页面地址
      this.allHost = value.replace(':443', '')
    },
    setPath(value: string) {
      // 设置当前页面路径（不包含语言）
      this.path = value
    },
    setMyCountry(payload: Readonly<{ myCountry: string }>) {
      this.myCountry = normalizeCountryCodeValue(payload.myCountry)
    },
    setCountryCode(payload: { country: string }) {
      const countryCode = normalizeCountryStoreCode(payload.country)
      this.CountryCode = countryCode
      /** SWR revalidate 时 response headers 已发送，SSR 不写 cookie，仅客户端写入。 */
      if (import.meta.client) {
        useAppCookie('CountryCode').value = resolveCountryCookieValue(this.CountryCode)
      }
    },
    setUsercookie(value: string) {
      // 获取用户登录cookie
      this.contentcookie = value
    },
    setLoginShow(value: boolean) {
      this.loginShow = value
    },
    setLoginverify(value: boolean) {
      this.loginverify = value
    },
    setUserlogin2(value: boolean) {
      this.userlogin2 = value
    },
    setNestinghref(value: string) {
      this.nestinghref = value
    },
    setuserCountry(value: UserCountryProfile) {
      this.usergetCountry = { ...value }
    },
    setriskcontrol(value: Record<string, any>) {
      // 设置屏蔽点位
      Object.assign(this.riskcontrol, value)
    },
    // skyline年份+国家+是否展示
    setskylinecheck(value: Record<string, any>) {
      this.skylinecheck = value
    },
    setlangsw(value: boolean) {
      this.islangsw = value
    },
    setlangshow(value: boolean) {
      // 设置语言切换是否显示源码
      this.langshow = value
    },
    setvisibleList(value: any[]) {
      // 设置菜单导航可见区域的菜单项
      this.visibleList = [...value]
    },
    setinvisibleList(value: any[]) {
      // 设置菜单导航不可见区域的菜单项
      this.invisibleList = [...value]
    },
    setwordid(value: string) {
      this.wordid = value
    },
    getbfp(value: string) {
      // 指纹
      this.bfp = value
    },
    setSearchWord(value: string) {
      this.searchWord = value
    },
    setLangCode(payload: { lang: string; hostS: string }) {
      let lang = payload.lang ? payload.lang.toLowerCase() : ''
      // if (lang === 'zh-hk' || lang === 'zh-tw') {
      //   lang = 'zh'
      // }
      this.LanguageCode = isLocaleCode(lang) && this.langList.includes(lang) ? lang : LocaleCode.En
    },
    setMenu(value: any[]) {
      // 设置菜单数据
      this.menuData = [...value]
    },
    setnavList(value: any[]) {
      // 设置菜单
      this.navList = [...value]
    },
    setLivestate(value: Record<string, any>) {
      // 获取直播状态
      // state.livestate = value
      Object.assign(this.livestate, value)
    },
    setcookieHost(value: string) {
      // 处理好存cookie需要的路径域名.wikifxen.com
      const host1 = [
        'fxeye688.com',
        'fxeye.com',
        'fxeyevn.com',
        'fxeyevi.com',
        'fxeye555.com',
        'fxeye2021.com',
        'fxeyefx.com',
        'fxeyevps.com',
        'wikifx.hk',
      ] // 普通fxeye域名
      const host2 = [
        'wikifxtips.com',
        'wikifx25.com',
        'fxeye25.com',
        'fxeyevip.com',
        'fxeyetips.com',
        'fxeyema.com',
        'fxeyeka.com',
        'fxeyecn.com',
        'wikifxvn.com',
        'wikifxvi.com',
        'wikifxen.com',
        'wikifxzh.com',
        'wikifxka.com',
        'wikifxcn.com',
      ] // 特殊wikifx域名
      const host3 = ['fxeye110.com', 'fxeye123.com', 'fxeye777.com'] // 特殊fxeye域名[推广]
      const host4 = [
        'wikifx.com',
        'fxeye2017.com',
        'wikifx2.com',
        'www.shareowen.com',
        'www.mchgi.com',
        'skylineguide.org',
      ] //  wikifx域名
      this.cookieHost = value
      let urlBool: string = ''
      if (
        !value.includes('.') ||
        value.includes('localhost') ||
        value.includes('127.0.0.1') ||
        value.includes('192.168.1.')
      ) {
        urlBool = value
      } else if (value.split('.').length <= 2) {
        urlBool = value.split('.')[1] ?? ''
      } else if (value.split('.').length >= 3) {
        if (value.includes('.com.cn')) {
          urlBool = value.split('.')[1] + '.' + value.split('.')[2] + '.' + value.split('.')[3]
        } else {
          urlBool = value.split('.')[1] + '.' + value.split('.')[2]
        }
      }
      if (host4.includes(urlBool)) {
        this.isWiki = '1'
      }
      if (host1.includes(urlBool)) {
        // 包含fxeye
        this.isWiki = '2'
      }
      if (host2.includes(urlBool)) {
        this.isWiki = '3'
      }
      if (host3.includes(urlBool)) {
        // 是否是特殊的fxeye域名
        this.isWiki = '4'
      }
      /** SWR revalidate 时 response headers 已发送，SSR 不写 cookie，仅客户端写入。 */
      if (import.meta.client) {
        useAppCookie('isWiki').value = this.isWiki
      }
    },
    setUser(value: Partial<AppUser>) {
      const nextUser = Object.keys(value).length === 0 ? null : ({ ...this.user, ...value } as AppUser)
      this.user = nextUser

      if (import.meta.server) {
        return
      }

      const userIdCookie = useAppCookie('userid')
      userIdCookie.value = nextUser?.userId ? String(nextUser.userId) : null

      /** 客户端额外缓存一份用户快照，便于后续页面或逻辑直接复用。 */
      try {
        if (nextUser) {
          window.localStorage.setItem('shared-app-user', JSON.stringify(nextUser))
        } else {
          window.localStorage.removeItem('shared-app-user')
        }
      } catch {
        // 浏览器禁用 localStorage 时静默降级，避免阻断登录态同步。
      }
    },
  },
})
